import { useState } from "react";
import { MessageCircle, Mail, Clock, HelpCircle } from "lucide-react";
import WaitlistModal from "@/components/WaitlistModal";
import SupportEmailButton from "@/components/SupportEmailButton";
import { useLanguage } from "@/context/LanguageContext";
import { openContactModal } from "@/components/ContactModal";

const SUPPORT_EMAIL = "bookings@karinelauzon.com";
const MESSENGER_URL = "https://m.me/PreciseRNInjectionist";

/**
 * Reusable "Can't find a date you want?" help card.
 *
 * Renders the same three CTAs (Messenger / Email / Join Waitlist) everywhere
 * a client interacts with booking — including the booking modal, the
 * booking page header, and membership/plan flows.
 *
 * Variants:
 *   - "dark"    (default): champagne-gold on dark — matches the booking modal
 *   - "light":  white background — matches on-page content sections
 *   - "inline": ultra-compact, single line — use inside step headers
 *
 * Usage:
 *   <DateFallbackHelp />
 *   <DateFallbackHelp variant="light" />
 */
export default function DateFallbackHelp({ variant = "dark", className = "" }) {
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const { language } = useLanguage();
  const isFr = language === "fr";

  // Color tokens per variant
  const tokens = {
    dark: {
      wrapper: "bg-gradient-to-br from-amber-950/30 via-zinc-900 to-zinc-900 border border-amber-500/40",
      label:   "text-amber-400",
      title:   "text-white",
      body:    "text-zinc-300",
      btn:     "bg-amber-500 hover:bg-amber-400 text-black",
      ghost:   "bg-zinc-800/70 hover:bg-zinc-700 text-zinc-100 border border-zinc-700",
      messenger: "bg-[#0084FF]/10 hover:bg-[#0084FF]/20 border border-[#0084FF]/40 text-[#67b6ff]",
    },
    light: {
      wrapper: "bg-gradient-to-br from-amber-50 via-rose-50/40 to-amber-50 border border-amber-200",
      label:   "text-amber-700",
      title:   "text-zinc-900",
      body:    "text-zinc-600",
      btn:     "bg-amber-500 hover:bg-amber-600 text-white",
      ghost:   "bg-white hover:bg-zinc-50 text-zinc-800 border border-zinc-200",
      messenger: "bg-[#0084FF]/5 hover:bg-[#0084FF]/15 border border-[#0084FF]/30 text-[#0084FF]",
    },
  };
  const t = tokens[variant] || tokens.dark;

  const subject = isFr
    ? "J'ai besoin d'aide pour trouver une date de rendez-vous"
    : "I need help finding an appointment date";

  return (
    <>
      <div
        className={`rounded-xl p-4 ${t.wrapper} ${className}`}
        data-testid="date-fallback-help"
      >
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-9 h-9 rounded-full bg-amber-500/20 flex items-center justify-center">
            <HelpCircle className="w-5 h-5 text-amber-500" />
          </div>
          <div className="min-w-0 flex-1">
            <p className={`text-[10px] font-bold uppercase tracking-[2px] ${t.label}`}>
              {isFr ? "Pas de date disponible ?" : "Can't find a date you want?"}
            </p>
            <h4
              className={`text-sm sm:text-base font-semibold mt-0.5 leading-snug ${t.title}`}
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {isFr
                ? "Envoyez-nous un message — on fera tout notre possible pour vous trouver une date"
                : "Send us a message — we'll do our best to find you the date you need"}
            </h4>
            <p className={`text-xs mt-1.5 leading-relaxed ${t.body}`}>
              {isFr
                ? "Message Facebook ou courriel — réponse en moins d'une heure. Inscrivez-vous aussi à la liste d'attente : si une place se libère à votre date préférée, nous vous contactons en premier."
                : "Message us on Facebook or email — we usually reply within an hour. You can also join the waitlist: if a spot opens on your preferred day & time, you'll be contacted first."}
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <a
                href={MESSENGER_URL} onClick={(e) => { e.preventDefault(); openContactModal({ source: "messenger-cta" }); }}
                target="_blank"
                rel="noopener noreferrer"
                className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-colors ${t.messenger}`}
                data-testid="fallback-messenger-btn"
              >
                <MessageCircle className="w-3.5 h-3.5" />
                {isFr ? "Messenger" : "Message on Facebook"}
              </a>
              <SupportEmailButton
                email={SUPPORT_EMAIL}
                subject={subject}
                label={SUPPORT_EMAIL}
                data-testid="fallback-email-btn"
                variant={variant === "light" ? "ghost" : "ghost"}
                className=""
              />
              <button
                type="button"
                onClick={() => setWaitlistOpen(true)}
                className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold transition-colors ${t.btn}`}
                data-testid="fallback-waitlist-btn"
              >
                <Clock className="w-3.5 h-3.5" />
                {isFr ? "Rejoindre la liste" : "Join the Waitlist"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <WaitlistModal isOpen={waitlistOpen} onClose={() => setWaitlistOpen(false)} />
    </>
  );
}
